import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
  Chip,
  Stack,
  CircularProgress,
  Backdrop,
} from '@mui/material';
import {
  ExpandMore,
  CheckCircle,
  PlayArrow,
} from '@mui/icons-material';
import { Exercise } from '../types/workout';
import { useWorkout } from '../hooks/useWorkout';
import { HeaderBar } from './HeaderBar';
import SetTracker from './SetTracker';
import { WorkoutDay } from '../types/workout';
import { RPE } from '../types/workout';

export const ExerciseInfo: React.FC = () => {
  const { exerciseIndex } = useParams<{ exerciseIndex: string }>();
  const navigate = useNavigate();
  
  const { 
    getCurrentWeekExercises, 
    currentWorkoutState, 
    initializeCurrentWorkout,
    updateCurrentSet,
    completeCurrentExercise,
    isExerciseCompleted,
    resetCurrentWorkout,
    isLoading,
    userProgress
  } = useWorkout();

  const [hasInitialized, setHasInitialized] = useState(false);

  const exerciseIdx = exerciseIndex ? parseInt(exerciseIndex) : -1;
  const currentExercises = getCurrentWeekExercises();
  const exercise = Array.isArray(currentExercises) && exerciseIdx >= 0 
    ? currentExercises[exerciseIdx] 
    : null;

  // Helper function to generate sets (same as in useWorkout hook)
  const generateSetsForExercise = useCallback((exercise: Exercise) => {
    const sets = [];
    
    // Parse warmup sets
    const warmupMatch = exercise.warmup_sets.match(/(\d+)(?:-(\d+))?/);
    const warmupCount = warmupMatch ? parseInt(warmupMatch[1]) : 0;
    
    for (let i = 0; i < warmupCount; i++) {
      sets.push({
        setNumber: i + 1,
        type: 'warmup' as const,
        weight: null,
        reps: null,
        completed: false
      });
    }
    
    // Parse working sets
    const workingMatch = exercise.working_sets.match(/(\d+)(?:-(\d+))?/);
    const workingCount = workingMatch ? parseInt(workingMatch[1]) : 0;
    
    for (let i = 0; i < workingCount; i++) {
      sets.push({
        setNumber: i + 1,
        type: 'working' as const,
        weight: null,
        reps: null,
        completed: false
      });
    }
    
    return sets;
  }, []);

  // Initialize workout when component mounts, but only if:
  // 1. Not currently loading
  // 2. We have exercises
  // 3. No current workout state exists
  // 4. We haven't already tried to initialize
  useEffect(() => {
    if (!isLoading && Array.isArray(currentExercises) && currentExercises.length > 0 && !hasInitialized) {
      if (!currentWorkoutState) {
        console.log('🏋️ Initializing current workout for exercise view...');
        initializeCurrentWorkout(currentExercises);
      }
      setHasInitialized(true);
    }
  }, [isLoading, currentExercises, currentWorkoutState, initializeCurrentWorkout, hasInitialized]);

  const currentExerciseProgress = currentWorkoutState?.exercises.find(
    ex => ex.exerciseIndex === exerciseIdx
  );

  // Only create temporary progress if we're not loading and there's no saved state
  const tempExerciseProgress = !isLoading && !currentExerciseProgress && exercise ? {
    exerciseIndex: exerciseIdx,
    exerciseName: exercise.exercise,
    sets: generateSetsForExercise(exercise),
    completed: false
  } : null;

  const displayExerciseProgress = currentExerciseProgress || tempExerciseProgress;

  const handleClose = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  const handleCompleteExercise = useCallback(async () => {
    if (exerciseIdx >= 0) {
      await completeCurrentExercise(exerciseIdx);
      navigate(-1);
    }
  }, [exerciseIdx, completeCurrentExercise, navigate]);

  const handleUpdateSet = useCallback(async (setIndex: number, weight: number, reps: number, actualRpe?: RPE) => {
    if (exerciseIdx >= 0) {
      await updateCurrentSet(exerciseIdx, setIndex, weight, reps, actualRpe);
    }
  }, [exerciseIdx, updateCurrentSet]);

  const exerciseCompleted = exerciseIdx >= 0 ? isExerciseCompleted(exerciseIdx) : false;
  const allSetsCompleted = displayExerciseProgress ? 
    (displayExerciseProgress.sets as any[]).every((set: any) => set.completed) : false;

  // Debug logging
  useEffect(() => {
    if (displayExerciseProgress) {
      console.log('🔍 Exercise Progress Debug:', {
        exerciseIdx,
        exerciseCompleted,
        allSetsCompleted,
        sets: displayExerciseProgress.sets,
        completedSets: (displayExerciseProgress.sets as any[]).filter((set: any) => set.completed),
        totalSets: displayExerciseProgress.sets.length
      });
    }
  }, [displayExerciseProgress, exerciseCompleted, allSetsCompleted, exerciseIdx]);

  // Helper function to extract YouTube video ID from URL
  const getYouTubeVideoId = (url: string) => {
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    return (match && match[2].length === 11) ? match[2] : null;
  };

  // Common accordion styles
  const accordionStyles = {
    borderRadius: '24px !important', 
    '&:before': { display: 'none' },
    '&.Mui-expanded': { borderRadius: '24px !important' },
    '& .MuiAccordionSummary-root': { 
      borderRadius: '24px !important',
      '&.Mui-expanded': { borderRadius: '24px 24px 0 0 !important' }
    },
    '& .MuiAccordionDetails-root': { 
      borderRadius: '0 0 24px 24px !important' 
    },
    overflow: 'hidden'
  };

  // Show loading backdrop while loading
  if (isLoading) {
    return (
      <Backdrop 
        open 
        sx={{ 
          color: '#fff', 
          zIndex: (theme) => theme.zIndex.drawer + 1,
          bgcolor: 'background.default',
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
        }}
      >
        <Stack alignItems="center" spacing={2}>
          <CircularProgress color="inherit" size={60} />
          <Typography variant="h6">Loading exercise data...</Typography>
        </Stack>
      </Backdrop>
    );
  }

  // Show exercise not found only after loading is complete
  if (!exercise) {
    return (
      <Box sx={{ bgcolor: 'background.default', minHeight: '100vh' }}>
        <HeaderBar title="Exercise Not Found" onBack={handleClose} showBackButton={true} />
        <Container maxWidth="md" sx={{ mt: 'calc(56px + env(safe-area-inset-top, 0px) + 24px)', pb: 4 }}>
          <Alert severity="error" sx={{ mt: 4 }}>
            <Typography variant="h6" gutterBottom>Exercise Not Found</Typography>
            <Typography>The requested exercise could not be found.</Typography>
          </Alert>
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh' }}>
      <HeaderBar title={exercise.exercise} onBack={handleClose} showBackButton={true} />
      <Container maxWidth="md" sx={{ mt: 'calc(56px + env(safe-area-inset-top, 0px) + 24px)', pb: 4 }}>
        <Stack spacing={3} sx={{ overflow: 'visible' }}>
          {/* Set Tracker - Show when we have exercise progress and not completed */}
          {displayExerciseProgress && !exerciseCompleted && (
            <SetTracker
              sets={displayExerciseProgress.sets}
              onUpdateSet={handleUpdateSet}
              targetReps={exercise.reps}
              exercise={exercise}
              userProgress={userProgress}
              currentWeek={currentWorkoutState?.week || userProgress?.currentWeek || 1}
              currentDay={currentWorkoutState?.day || userProgress?.currentDay || WorkoutDay.MONDAY}
            />
          )}

          {/* Complete Exercise Button */}
          {allSetsCompleted && !exerciseCompleted && (
            <Card sx={{ bgcolor: 'success.dark', color: 'success.contrastText' }}>
              <CardContent>
                <Button
                  onClick={handleCompleteExercise}
                  variant="contained"
                  color="success"
                  size="large"
                  fullWidth
                  startIcon={<CheckCircle />}
                  sx={{ py: 2 }}
                >
                  Complete Exercise
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Exercise Completed Status */}
          {exerciseCompleted && (
            <Alert severity="success" sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography variant="h6" fontWeight="bold">
                Exercise Completed!
              </Typography>
            </Alert>
          )}

          {/* Exercise Info */}
          {exercise.exercise_video && (
            <Accordion sx={accordionStyles}>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography variant="h6" fontWeight="bold">
                  Exercise Info
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Stack spacing={2}>
                  {/* Notes inside Exercise Info */}
                  {exercise.notes && (
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="h6" fontWeight="bold" gutterBottom>
                          Notes
                        </Typography>
                        <Typography variant="body2" sx={{ lineHeight: 1.6, fontSize: '0.875rem' }}>
                          {exercise.notes}
                        </Typography>
                      </CardContent>
                    </Card>
                  )}

                  {/* Intensity Technique */}
                  {exercise.last_set_intensity_technique !== 'none' && (
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="h6" fontWeight="bold" gutterBottom>
                          Intensity Technique
                        </Typography>
                        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                          <Chip 
                            label={exercise.last_set_intensity_technique} 
                            color="warning" 
                            sx={{ textTransform: 'capitalize' }}
                          />
                        </Box>
                      </CardContent>
                    </Card>
                  )}
                  
                  {/* Exercise Video */}
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="h6" fontWeight="bold" gutterBottom>
                        Exercise Video
                      </Typography>
                      {(() => {
                        const videoId = getYouTubeVideoId(exercise.exercise_video);
                        if (videoId) {
                          return (
                            <Box sx={{ position: 'relative', width: '100%', paddingBottom: '56.25%' }}>
                              <iframe
                                style={{
                                  position: 'absolute',
                                  top: 0,
                                  left: 0,
                                  width: '100%',
                                  height: '100%',
                                  borderRadius: 8,
                                }}
                                src={`https://www.youtube.com/embed/${videoId}`}
                                title="Exercise Video"
                                frameBorder="0"
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                allowFullScreen
                              />
                            </Box>
                          );
                        } else {
                          return (
                            <Button
                              href={exercise.exercise_video}
                              target="_blank"
                              rel="noopener noreferrer"
                              variant="outlined"
                              startIcon={<PlayArrow />}
                              fullWidth
                            >
                              Open Exercise Video
                            </Button>
                          );
                        }
                      })()}
                    </CardContent>
                  </Card>
                </Stack>
              </AccordionDetails>
            </Accordion>
          )}

          {/* Substitution Options */}
          {(exercise.substitution_option_1 || exercise.substitution_option_2) && (
            <Accordion sx={accordionStyles}>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography variant="h6" fontWeight="bold">
                  Alternative Exercises
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Stack spacing={2}>
                  {exercise.substitution_option_1 && (
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="h6" fontWeight="bold" gutterBottom>
                          {exercise.substitution_option_1.name}
                        </Typography>
                        {(() => {
                          const videoId = getYouTubeVideoId(exercise.substitution_option_1.link);
                          if (videoId) {
                            return (
                              <Box sx={{ position: 'relative', width: '100%', paddingBottom: '56.25%' }}>
                                <iframe
                                  style={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    width: '100%',
                                    height: '100%',
                                    borderRadius: 8,
                                  }}
                                  src={`https://www.youtube.com/embed/${videoId}`}
                                  title={`${exercise.substitution_option_1.name} Video`}
                                  frameBorder="0"
                                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                  allowFullScreen
                                />
                              </Box>
                            );
                          } else {
                            return (
                              <Button
                                href={exercise.substitution_option_1.link}
                                target="_blank"
                                rel="noopener noreferrer"
                                variant="outlined"
                                size="small"
                                startIcon={<PlayArrow />}
                              >
                                Watch Video
                              </Button>
                            );
                          }
                        })()}
                      </CardContent>
                    </Card>
                  )}
                  {exercise.substitution_option_2 && (
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="h6" fontWeight="bold" gutterBottom>
                          {exercise.substitution_option_2.name}
                        </Typography>
                        {(() => {
                          const videoId = getYouTubeVideoId(exercise.substitution_option_2.link);
                          if (videoId) {
                            return (
                              <Box sx={{ position: 'relative', width: '100%', paddingBottom: '56.25%' }}>
                                <iframe
                                  style={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    width: '100%',
                                    height: '100%',
                                    borderRadius: 8,
                                  }}
                                  src={`https://www.youtube.com/embed/${videoId}`}
                                  title={`${exercise.substitution_option_2.name} Video`}
                                  frameBorder="0"
                                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                  allowFullScreen
                                />
                              </Box>
                            );
                          } else {
                            return (
                              <Button
                                href={exercise.substitution_option_2.link}
                                target="_blank"
                                rel="noopener noreferrer"
                                variant="outlined"
                                size="small"
                                startIcon={<PlayArrow />}
                              >
                                Watch Video
                              </Button>
                            );
                          }
                        })()}
                      </CardContent>
                    </Card>
                  )}
                </Stack>
              </AccordionDetails>
            </Accordion>
          )}
        </Stack>
      </Container>
    </Box>
  );
}; 